import { RTCNotification } from 'rtc-protocol';
import { SignalClient } from './connection/SignalClient.js';
import { SocketClient } from './connection/SocketClient.js';
import { Camera } from './media/Camera.js';
import { Microphone } from './media/Microphone.js';
import { Screen } from './media/Screen.js';
import type {
  CreateRoomOptions,
  MediaKind,
  MediaOptions,
  ProducerInfo,
  RTCClientEventMap,
  RTCClientOptions,
  RTCNotificationEnvelope,
  RoomAPI,
  RoomInfo
} from './types/index.js';
import { EventEmitter, type EventListener } from './utils/EventEmitter.js';
import { toError } from './utils/browser.js';
import { PeerConnection } from './webrtc/PeerConnection.js';

export class RTCClient {
  readonly camera = new Camera();
  readonly microphone = new Microphone();
  readonly screen = new Screen();
  readonly media: {
    camera: Camera;
    microphone: Microphone;
    screen: Screen;
    getLocalStream: (options?: MediaOptions) => Promise<MediaStream>;
    toggleAudio: (enabled: boolean) => Promise<void>;
    toggleVideo: (enabled: boolean) => Promise<void>;
  };
  readonly room: RoomAPI;

  currentRoomId: string | null = null;

  private readonly events = new EventEmitter<RTCClientEventMap>();
  private readonly socket: SocketClient;
  private readonly signal: SignalClient;
  private readonly peer: PeerConnection;
  private readonly joinedRooms = new Set<string>();
  private readonly pendingProducers = new Map<string, ProducerInfo>();
  private localStream: MediaStream | null = null;
  private userId: string | undefined;
  private stoppingScreen = false;

  constructor(options: RTCClientOptions) {
    this.socket = new SocketClient(options);
    this.signal = new SignalClient(this.socket);
    this.peer = new PeerConnection(this.signal, {
      onRemoteStream: (userId, stream) => this.events.emit('stream.remote', { userId, stream }),
      onProducerClosed: (data) => this.events.emit('producer.closed', data),
      onError: (error) => this.reportError(error)
    });

    this.media = {
      camera: this.camera,
      microphone: this.microphone,
      screen: this.screen,
      getLocalStream: (mediaOptions = { audio: true, video: true }) => this.captureMedia(mediaOptions),
      toggleAudio: (enabled) => enabled ? this.unmuteAudio() : this.muteAudio(),
      toggleVideo: (enabled) => enabled ? this.unmuteVideo() : this.muteVideo()
    };
    this.room = {
      getRoomInfo: (roomId) => this.signal.getRoomInfo(roomId),
      getRoomMembers: (roomId) => this.signal.getRoomMembers(roomId),
      listRooms: () => this.signal.listRooms(),
      destroyRoom: (roomId) => this.destroyRoom(roomId),
      getJoinedRooms: () => [...this.joinedRooms],
      isInRoom: (roomId) => this.joinedRooms.has(roomId),
      leaveAllRooms: () => this.leaveAllRooms()
    };

    this.bindSocketEvents();
  }

  on<K extends keyof RTCClientEventMap & string>(
    event: K,
    listener: EventListener<RTCClientEventMap[K]>
  ): () => void {
    return this.events.on(event, listener);
  }

  once<K extends keyof RTCClientEventMap & string>(
    event: K,
    listener: EventListener<RTCClientEventMap[K]>
  ): () => void {
    return this.events.once(event, listener);
  }

  off<K extends keyof RTCClientEventMap & string>(
    event: K,
    listener: EventListener<RTCClientEventMap[K]>
  ): void {
    this.events.off(event, listener);
  }

  async connect(): Promise<void> {
    try {
      await this.socket.connect();
    } catch (error) {
      const rtcError = toError(error, 'Failed to connect to RTC server');
      this.reportError(rtcError);
      throw rtcError;
    }
  }

  disconnect(): void {
    this.resetMediaSession();
    this.socket.disconnect();
  }

  isConnected(): boolean {
    return this.socket.isConnected();
  }

  setUserId(userId: string): void {
    if (!userId?.trim()) throw new Error('userId is required');
    this.userId = userId.trim();
  }

  getUserId(): string | undefined {
    return this.userId;
  }

  async createRoom(options: CreateRoomOptions = {}): Promise<RoomInfo> {
    await this.ensureConnected();
    this.assertCanEnterRoom();
    const { media, ...roomOptions } = options;
    const room = await this.signal.createRoom(roomOptions);
    try {
      await this.enterRoom(room, this.resolveMediaOptions(media, room.type));
      return room;
    } catch (error) {
      await this.safeLeave(room.roomId);
      this.resetMediaSession();
      throw error;
    }
  }

  async joinRoom(roomId: string, media?: MediaOptions): Promise<RoomInfo> {
    await this.ensureConnected();
    this.assertCanEnterRoom(roomId);
    const room = await this.signal.join(this.requireId(roomId, 'roomId'));
    try {
      await this.enterRoom(room, this.resolveMediaOptions(media, room.type));
      return room;
    } catch (error) {
      await this.safeLeave(room.roomId);
      this.resetMediaSession();
      throw error;
    }
  }

  async startCall(roomId?: string, media?: MediaOptions): Promise<RoomInfo> {
    if (roomId) return this.joinRoom(roomId, media);
    return this.createRoom({ type: media?.video === false ? 'voice' : 'video', media });
  }

  async leaveRoom(roomId = this.currentRoomId ?? ''): Promise<{ roomId: string; left: boolean }> {
    const id = this.requireId(roomId, 'roomId');
    try {
      return await this.signal.leave(id);
    } finally {
      this.joinedRooms.delete(id);
      if (this.currentRoomId === id) this.resetMediaSession();
    }
  }

  async endCall(): Promise<void> {
    const callId = this.currentRoomId;
    if (!callId) {
      this.resetMediaSession();
      return;
    }

    try {
      await this.signal.end(callId);
      this.events.emit('call.ended', { callId, endedBy: this.userId });
    } finally {
      this.resetMediaSession();
    }
  }

  async destroyRoom(roomId: string): Promise<{ roomId: string; destroyed: boolean }> {
    const id = this.requireId(roomId, 'roomId');
    try {
      return await this.signal.destroy(id);
    } finally {
      this.joinedRooms.delete(id);
      if (this.currentRoomId === id) this.resetMediaSession();
    }
  }

  startVoiceCall(targetId: string): Promise<{ callId: string }> {
    return this.signal.invite(this.requireId(targetId, 'targetId'), 'voice');
  }

  startVideoCall(targetId: string): Promise<{ callId: string }> {
    return this.signal.invite(this.requireId(targetId, 'targetId'), 'video');
  }

  acceptCall(callId: string): Promise<{ callId: string; accepted: boolean }> {
    return this.signal.accept(this.requireId(callId, 'callId'));
  }

  rejectCall(callId: string): Promise<{ callId: string; rejected: boolean }> {
    return this.signal.reject(this.requireId(callId, 'callId'));
  }

  endCall1v1(callId: string): Promise<{ callId: string; ended: boolean }> {
    return this.signal.end(this.requireId(callId, 'callId'));
  }

  async muteAudio(): Promise<void> {
    await this.setMediaMuted('audio', true);
  }

  async unmuteAudio(): Promise<void> {
    await this.setMediaMuted('audio', false);
  }

  async muteVideo(): Promise<void> {
    await this.setMediaMuted('video', true);
  }

  async unmuteVideo(): Promise<void> {
    await this.setMediaMuted('video', false);
  }

  async shareScreen(options?: DisplayMediaStreamOptions): Promise<MediaStream> {
    if (!this.currentRoomId || !this.peer.ready) throw new Error('Join a room before sharing the screen');
    const stream = await this.screen.start(options);
    const track = this.screen.getTrack();
    if (!track) throw new Error('Screen capture did not provide a video track');

    track.onended = () => {
      void this.stopScreenShare().catch((error) => this.reportError(toError(error)));
    };
    try {
      await this.peer.replaceVideoTrack(track);
    } catch (error) {
      track.onended = null;
      this.screen.stop();
      throw error;
    }
    this.events.emit('screen.shareStarted', stream);
    return stream;
  }

  async stopScreenShare(): Promise<void> {
    if (this.stoppingScreen || !this.screen.getStream()) return;
    this.stoppingScreen = true;
    try {
      const screenTrack = this.screen.getTrack();
      if (screenTrack) screenTrack.onended = null;
      this.screen.stop();
      const cameraTrack = this.camera.getTrack();
      if (cameraTrack?.readyState === 'live' && this.peer.ready) {
        await this.peer.replaceVideoTrack(cameraTrack);
      } else if (this.peer.ready) {
        await this.peer.closeProducer('video');
      }
      this.events.emit('screen.shareStopped', undefined);
    } finally {
      this.stoppingScreen = false;
    }
  }

  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  getRemoteStream(userId: string): MediaStream | null {
    return this.peer.getRemoteStream(userId);
  }

  getAllRemoteStreams(): Map<string, MediaStream> {
    return this.peer.getAllRemoteStreams();
  }

  private bindSocketEvents(): void {
    this.socket.on('connecting', () => this.events.emit('connecting', undefined));
    this.socket.on('connected', () => this.events.emit('connected', undefined));
    this.socket.on('disconnected', (reason) => {
      this.resetMediaSession();
      this.events.emit('disconnected', reason);
    });
    this.socket.on('reconnect_failed', () => this.events.emit('reconnect_failed', undefined));
    this.socket.on('error', (error) => this.reportError(error));
    this.socket.on('message', (message) => this.handleNotification(message));
  }

  private handleNotification({ event, data }: RTCNotificationEnvelope): void {
    this.events.emit(event, data);

    switch (event) {
      case RTCNotification.INVITED:
        this.events.emit('call.invited', data as RTCClientEventMap['call.invited']);
        break;
      case RTCNotification.ACCEPTED:
        this.events.emit('call.accepted', data as RTCClientEventMap['call.accepted']);
        break;
      case RTCNotification.REJECTED:
        this.events.emit('call.rejected', data as RTCClientEventMap['call.rejected']);
        break;
      case RTCNotification.ENDED:
        this.events.emit('call.ended', data as RTCClientEventMap['call.ended']);
        this.resetMediaSession();
        break;
      case RTCNotification.ROOM_CREATED:
        this.events.emit('room.created', data as RoomInfo);
        break;
      case RTCNotification.ROOM_DESTROYED:
        this.events.emit('room.destroyed', data as RTCClientEventMap['room.destroyed']);
        if ((data as { roomId?: string })?.roomId === this.currentRoomId) this.resetMediaSession();
        break;
      case RTCNotification.MEMBER_JOINED:
        this.events.emit('member.joined', data as RTCClientEventMap['member.joined']);
        break;
      case RTCNotification.MEMBER_LEFT: {
        const member = data as RTCClientEventMap['member.left'];
        this.peer.removeUser(member.userId);
        this.events.emit('member.left', member);
        break;
      }
      case RTCNotification.MEMBER_MUTED:
        this.events.emit('member.muted', data as RTCClientEventMap['member.muted']);
        break;
      case RTCNotification.MEMBER_UNMUTED:
        this.events.emit('member.unmuted', data as RTCClientEventMap['member.unmuted']);
        break;
      case RTCNotification.NEW_PRODUCER:
        this.handleNewProducer(data as ProducerInfo & { roomId: string });
        break;
      case RTCNotification.PRODUCER_CLOSED: {
        const producer = data as RTCClientEventMap['producer.closed'];
        const wasPending = this.pendingProducers.delete(producer.producerId);
        const wasConsuming = this.peer.closeRemoteProducer(producer.producerId);
        if (wasPending || wasConsuming) this.events.emit('producer.closed', producer);
        break;
      }
    }
  }

  private handleNewProducer(info: ProducerInfo & { roomId: string }): void {
    this.events.emit('producer.new', info);
    if (info.roomId !== this.currentRoomId) return;
    if (!this.peer.ready) {
      this.pendingProducers.set(info.producerId, info);
      return;
    }
    void this.peer.consume(info).catch((error) => this.reportError(toError(error)));
  }

  private async enterRoom(room: RoomInfo, mediaOptions: Required<MediaOptions>): Promise<void> {
    this.currentRoomId = room.roomId;
    this.joinedRooms.add(room.roomId);
    const localStream = await this.captureMedia(mediaOptions);
    await this.peer.initialize(room.roomId);

    for (const track of localStream.getTracks()) await this.peer.publishTrack(track);
    const existing = await this.signal.getProducers(room.roomId);
    const producers = new Map(existing.map((producer) => [producer.producerId, producer]));
    for (const producer of this.pendingProducers.values()) producers.set(producer.producerId, producer);
    this.pendingProducers.clear();
    await Promise.all([...producers.values()].map(async (producer) => {
      try {
        await this.peer.consume(producer);
      } catch (error) {
        this.reportError(toError(error));
      }
    }));
  }

  private async captureMedia(options: MediaOptions): Promise<MediaStream> {
    const audio = options.audio ?? true;
    const video = options.video ?? true;
    const tasks: Promise<unknown>[] = [];

    if (audio === false) this.microphone.stop();
    else tasks.push(this.microphone.start(audio));
    if (video === false) this.camera.stop();
    else tasks.push(this.camera.start(video));
    await Promise.all(tasks);

    const stream = new MediaStream();
    const audioTrack = this.microphone.getTrack();
    const videoTrack = this.camera.getTrack();
    if (audioTrack) stream.addTrack(audioTrack);
    if (videoTrack) stream.addTrack(videoTrack);
    this.localStream = stream;
    this.events.emit('stream.local', stream);
    return stream;
  }

  private async setMediaMuted(kind: MediaKind, muted: boolean): Promise<void> {
    const enabled = !muted;
    if (kind === 'audio') this.microphone.setEnabled(enabled);
    else {
      this.camera.setEnabled(enabled);
      const screenTrack = this.screen.getTrack();
      if (screenTrack) screenTrack.enabled = enabled;
    }
    await this.peer.setProducerPaused(kind, muted);
    if (this.currentRoomId) await this.signal.setMuted(this.currentRoomId, kind, muted);
    this.events.emit(`${kind}.${muted ? 'muted' : 'unmuted'}`, undefined);
  }

  private resolveMediaOptions(media: MediaOptions | undefined, type: RoomInfo['type']): Required<MediaOptions> {
    return {
      audio: media?.audio ?? true,
      video: media?.video ?? type === 'video'
    };
  }

  private async leaveAllRooms(): Promise<void> {
    for (const roomId of [...this.joinedRooms]) {
      try {
        await this.leaveRoom(roomId);
      } catch (error) {
        this.reportError(toError(error));
      }
    }
  }

  private resetMediaSession(): void {
    const screenTrack = this.screen.getTrack();
    if (screenTrack) screenTrack.onended = null;
    this.screen.stop();
    this.camera.stop();
    this.microphone.stop();
    this.peer.close();
    this.pendingProducers.clear();
    this.joinedRooms.clear();
    this.localStream = null;
    this.currentRoomId = null;
  }

  private async safeLeave(roomId: string): Promise<void> {
    try {
      if (this.socket.isConnected()) await this.signal.leave(roomId);
    } catch {
      // Preserve the original setup error; disconnect cleanup will release server resources.
    }
  }

  private async ensureConnected(): Promise<void> {
    if (!this.socket.isConnected()) await this.connect();
  }

  private assertCanEnterRoom(roomId?: string): void {
    if (this.currentRoomId && this.currentRoomId !== roomId) {
      throw new Error(`Already joined room ${this.currentRoomId}; leave it before joining another room`);
    }
  }

  private requireId(value: string, name: string): string {
    if (!value?.trim()) throw new Error(`${name} is required`);
    return value.trim();
  }

  private reportError(error: Error): void {
    this.events.emit('error', error);
  }
}

export default RTCClient;
